import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMotorcycle,
  faTaxi,
  faTruckFast,
  faTruckFront,
  faTruckPickup,
  faCircleNotch,
  faArrowLeft,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { requestPermission } from "../push-notifications";
import { updateUserInServiceWorker } from "../utils/serviceWorkerUtils";
import { encryptData } from "../utils/storageCrypto";
import { theme } from "../assets/theme/theme";

const icons = [
  faMotorcycle,
  faTaxi,
  faTruckFast,
  faTruckFront,
  faTruckPickup,
];

// -----------------------------------
// Animated Vehicle Icon (Hero Brand Box)
// -----------------------------------
const AnimatedVehicleIcon = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % icons.length);
    }, 1400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-center mb-6">
      <div
        className="relative w-16 h-16 flex items-center justify-center overflow-hidden transition-all shadow-md"
        style={{
          borderRadius: `${theme.radius[16]}px`,
          backgroundColor: theme.colors.brand,
          boxShadow: `0 8px 20px ${theme.colors.brand}35`,
        }}
      >
        {icons.map((icon, index) => (
          <FontAwesomeIcon
            key={index}
            icon={icon}
            className={`absolute text-2xl transition-all duration-500 ease-in-out ${
              index === activeIndex
                ? "opacity-100 transform scale-100"
                : "opacity-0 transform scale-75 pointer-events-none"
            }`}
            style={{ color: theme.colors.pureWhite }}
          />
        ))}
      </div>
    </div>
  );
};

// -----------------------------------
// OTP Verification Sub-component
// -----------------------------------
const OTPVerification = ({ mobile, onBack }) => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const VerifyOtpApi =
    process.env.REACT_APP_FORE_BRICKBUCKET +
    "delivery-partner/verify-otp-and-login";

  const isOtpValid = otp.every((d) => d.trim() !== "");

  const handleVerifyOtp = async () => {
    if (!isOtpValid || loading) return;

    try {
      setLoading(true);
      setError("");

      const res = await fetch(VerifyOtpApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile,
          otp: Number(otp.join("")),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Invalid OTP");
        return;
      }

      // Save token/session
      sessionStorage.setItem("token", encryptData(data.token));
      sessionStorage.setItem("deliveryPartner", encryptData(JSON.stringify(data)));
      sessionStorage.setItem("dpId", encryptData(data._id));
      sessionStorage.setItem("mobile", encryptData(data.mobile));
      requestPermission();

      const userId = data._id;
      updateUserInServiceWorker(userId);

      navigate(
        `/delivery/rides?mobile=${encodeURIComponent(mobile)}`,
        { state: { isBrickbucket: true } }
      );
    } catch (err) {
      setError("OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          type="button"
          className="flex items-center gap-1.5 transition-colors cursor-pointer"
          style={{
            ...theme.typography.bodySM,
            color: theme.colors.concrete,
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Change Number</span>
        </button>
        <span
          className="px-2.5 py-0.5 rounded-full"
          style={{
            ...theme.typography.monoMD,
            color: theme.colors.blueOxide,
            backgroundColor: theme.colors.offWhite,
          }}
        >
          +91 {mobile}
        </span>
      </div>

      <div className="text-center space-y-1">
        <h2
          style={{
            ...theme.typography.headingMD,
            color: theme.colors.bitumen,
          }}
        >
          Enter OTP
        </h2>
        <p
          style={{
            ...theme.typography.bodySM,
            color: theme.colors.concrete,
          }}
        >
          Enter the 4-digit code sent to your mobile
        </p>
      </div>

      {/* 4-Digit OTP Boxes */}
      <div className="flex justify-between gap-3 my-4">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            type="tel"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="w-14 h-14 text-center border transition-all outline-none"
            style={{
              ...theme.typography.monoLG,
              fontSize: "22px",
              borderRadius: `${theme.radius[12]}px`,
              borderColor: digit ? theme.colors.brand : theme.colors.grey002,
              backgroundColor: digit ? theme.colors.grey001 : theme.colors.pureWhite,
              color: theme.colors.bitumen,
            }}
          />
        ))}
      </div>

      {error && (
        <div
          className="p-3 rounded-xl text-center"
          style={{
            ...theme.typography.bodySM,
            backgroundColor: theme.colors.brandTint,
            color: theme.colors.error,
          }}
        >
          {error}
        </div>
      )}

      {/* Action Button */}
      <button
        disabled={!isOtpValid || loading}
        onClick={handleVerifyOtp}
        type="button"
        className="w-full flex items-center justify-center gap-2 cursor-pointer transition-all disabled:cursor-not-allowed"
        style={{
          ...theme.typography.buttonMD,
          paddingTop: `${theme.spacing[200]}px`,
          paddingBottom: `${theme.spacing[200]}px`,
          borderRadius: `${theme.radius[12]}px`,
          backgroundColor: isOtpValid ? theme.colors.brand : theme.colors.grey002,
          color: isOtpValid ? theme.colors.pureWhite : theme.colors.concrete,
          boxShadow: isOtpValid ? `0 4px 12px ${theme.colors.brand}40` : "none",
        }}
      >
        {loading ? (
          <>
            <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
            <span>Verifying...</span>
          </>
        ) : (
          "Verify & Login"
        )}
      </button>
    </div>
  );
};

// -----------------------------------
// Main Component
// -----------------------------------
const PartnerLogin = () => {
  const [mobile, setMobile] = useState("");
  const [step, setStep] = useState("MOBILE"); // 'MOBILE' | 'OTP'
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const LoginApi =
    process.env.REACT_APP_FORE_BRICKBUCKET +
    "delivery-partner/delivery-partner-login";

  const isValidMobile = mobile.length === 10;

  const handleLogin = async () => {
    if (!isValidMobile || loading) return;

    try {
      setLoading(true);
      setError("");

      const res = await fetch(LoginApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: mobile }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Login failed. Please check the number.");
        return;
      }

      setStep("OTP");
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: theme.colors.offWhite }}
    >
      <div
        className="w-full max-w-sm border transition-all shadow-sm"
        style={{
          backgroundColor: theme.colors.pureWhite,
          borderRadius: `${theme.radius[24]}px`,
          borderColor: theme.colors.grey002,
          padding: `${theme.spacing[400]}px`,
          boxShadow: "0 10px 30px rgba(12, 12, 13, 0.08)",
        }}
      >
        <AnimatedVehicleIcon />

        {step === "MOBILE" && (
          <div className="space-y-5">
            <div className="text-center space-y-1">
              <h1
                style={{
                  ...theme.typography.displayXL,
                  color: theme.colors.bitumen,
                }}
              >
                Delivery Partner
              </h1>
              <p
                style={{
                  ...theme.typography.bodySM,
                  color: theme.colors.concrete,
                }}
              >
                Enter your mobile number to sign in
              </p>
            </div>

            <div className="relative flex items-center">
              <span
                className="absolute left-3.5 border-r pr-2"
                style={{
                  ...theme.typography.monoMD,
                  color: theme.colors.concrete,
                  borderColor: theme.colors.grey002,
                }}
              >
                +91
              </span>
              <input
                type="tel"
                maxLength={10}
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                placeholder="Enter mobile number"
                className="w-full pl-16 pr-4 py-3.5 border transition-all outline-none"
                style={{
                  ...theme.typography.monoMD,
                  borderRadius: `${theme.radius[12]}px`,
                  borderColor: isValidMobile
                    ? theme.colors.brand
                    : theme.colors.grey002,
                  color: theme.colors.bitumen,
                  backgroundColor: theme.colors.grey001,
                }}
              />
            </div>

            {error && (
              <div
                className="p-3 rounded-xl text-center"
                style={{
                  ...theme.typography.bodySM,
                  backgroundColor: theme.colors.brandTint,
                  color: theme.colors.error,
                }}
              >
                {error}
              </div>
            )}

            <button
              disabled={!isValidMobile || loading}
              onClick={handleLogin}
              type="button"
              className="w-full flex items-center justify-center gap-2 cursor-pointer transition-all disabled:cursor-not-allowed"
              style={{
                ...theme.typography.buttonMD,
                paddingTop: `${theme.spacing[200]}px`,
                paddingBottom: `${theme.spacing[200]}px`,
                borderRadius: `${theme.radius[12]}px`,
                backgroundColor: isValidMobile
                  ? theme.colors.brand
                  : theme.colors.grey002,
                color: isValidMobile
                  ? theme.colors.pureWhite
                  : theme.colors.concrete,
                boxShadow: isValidMobile
                  ? `0 4px 12px ${theme.colors.brand}40`
                  : "none",
              }}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                  <span>Sending OTP...</span>
                </>
              ) : (
                "Get OTP"
              )}
            </button>

            <div className="flex items-center justify-center gap-1.5 pt-1 text-center">
              <FontAwesomeIcon
                icon={faShieldHalved}
                className="text-xs"
                style={{ color: theme.colors.concrete }}
              />
              <span
                style={{
                  ...theme.typography.bodySM,
                  color: theme.colors.concrete,
                  fontSize: "10px",
                }}
              >
                Secured Partner Access
              </span>
            </div>
          </div>
        )}

        {step === "OTP" && (
          <OTPVerification
            mobile={mobile}
            onBack={() => {
              setStep("MOBILE");
              setError("");
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PartnerLogin;